import React, { useRef, useEffect, useState } from 'react';
import {
  Box, Dialog, DialogContent, Button, Typography, IconButton, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import IosShareIcon from '@mui/icons-material/IosShare';
import { DateTime } from 'luxon';
import { PostData } from '../model/types';
import { hapticTap } from '../lib/haptics';

/**
 * DEATH CERTIFICATE
 *
 * A canvas-generated share card (1200×628, standard OG ratio) for
 * expired or dying posts. Gives users a tangible memento of
 * something that by design can't be saved. Download as PNG or share
 * via native share sheet.
 *
 * This is the viral engine for wipe.haus — people share death
 * certificates of their favorite posts as "I remember when…"
 * artifacts, pulling non-users into the site.
 *
 * Triggered from the post overflow menu ("Generate death certificate").
 */

function drawCard(
  canvas: HTMLCanvasElement,
  opts: {
    username: string;
    postText: string;
    createdAt: string;
    diedAt: string | null;
    voteCount: number;
    replyCount: number;
    isExpired: boolean;
  },
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const W = canvas.width;
  const H = canvas.height;

  // Background — dark gradient
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0a0a14');
  bg.addColorStop(1, '#1a1a2e');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Decorative noise — small purple dots
  ctx.fillStyle = 'rgba(124,77,255,0.08)';
  for (let i = 0; i < 80; i++) {
    ctx.beginPath();
    ctx.arc(Math.random() * W, Math.random() * H, Math.random() * 2, 0, Math.PI * 2);
    ctx.fill();
  }

  // Border
  ctx.strokeStyle = 'rgba(124,77,255,0.6)';
  ctx.lineWidth = 6;
  ctx.strokeRect(40, 40, W - 80, H - 80);
  ctx.strokeStyle = 'rgba(124,77,255,0.3)';
  ctx.lineWidth = 2;
  ctx.strokeRect(52, 52, W - 104, H - 104);

  // Skull / epitaph marker
  ctx.font = '64px "VT323", monospace';
  ctx.fillStyle = '#7c4dff';
  ctx.textAlign = 'center';
  ctx.fillText('⚰', W / 2, 140);

  // Title
  ctx.font = 'bold 48px "VT323", monospace';
  ctx.fillStyle = '#ffffff';
  ctx.fillText(opts.isExpired ? 'CERTIFICATE OF DEATH' : 'EPITAPH DRAFT', W / 2, 200);

  // Quote / post text — wrapped
  ctx.font = 'italic 32px Georgia, serif';
  ctx.fillStyle = '#e0e0e0';
  const words = (opts.postText.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1').replace(/#\[([^\]]+)\]\([^)]+\)/g, '#$1')).split(/\s+/);
  const maxWidth = W - 200;
  const lines: string[] = [];
  let current = '';
  for (const w of words) {
    const test = current ? current + ' ' + w : w;
    if (ctx.measureText(test).width > maxWidth) {
      if (current) lines.push(current);
      current = w;
    } else {
      current = test;
    }
    if (lines.length >= 4) break;
  }
  if (current && lines.length < 5) lines.push(current);
  const quoteStartY = 260;
  const lineHeight = 42;
  ctx.fillText('"', W / 2 - ctx.measureText(lines[0] ?? '').width / 2 - 18, quoteStartY);
  lines.forEach((line, i) => {
    ctx.fillText(line, W / 2, quoteStartY + i * lineHeight);
  });
  if (lines.length > 0) {
    const lastLine = lines[lines.length - 1];
    const lastY = quoteStartY + (lines.length - 1) * lineHeight;
    ctx.fillText('"', W / 2 + ctx.measureText(lastLine).width / 2 + 10, lastY);
  }

  // Metadata block
  const metaY = 480;
  ctx.font = '22px "VT323", monospace';
  ctx.fillStyle = '#b79dff';
  ctx.textAlign = 'center';
  const born = DateTime.fromISO(opts.createdAt);
  const bornStr = born.isValid ? born.toFormat('LLL dd, yyyy') : '?';
  const died = opts.diedAt ? DateTime.fromISO(opts.diedAt) : null;
  const diedStr = died?.isValid ? died.toFormat('LLL dd, yyyy') : (opts.isExpired ? 'today' : 'soon');
  ctx.fillText(`BORN ${bornStr.toUpperCase()}   —   DIED ${diedStr.toUpperCase()}`, W / 2, metaY);

  // Stats row
  ctx.font = '26px "VT323", monospace';
  ctx.fillStyle = '#ffffff';
  const stats = `${opts.voteCount} votes · ${opts.replyCount} replies`;
  ctx.fillText(stats, W / 2, metaY + 40);

  // Author
  ctx.font = '28px "VT323", monospace';
  ctx.fillStyle = '#7c4dff';
  ctx.fillText(`@${opts.username}`, W / 2, metaY + 86);

  // Footer branding
  ctx.font = '18px "VT323", monospace';
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.fillText('wipe.haus — fame is fleeting', W / 2, H - 70);
}

function DeathCertificate({
  post,
  username,
  open,
  onClose,
}: {
  post: PostData;
  username: string;
  open: boolean;
  onClose: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dataUrl, setDataUrl] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    const c = canvasRef.current;
    if (!c) return;
    drawCard(c, {
      username,
      postText: post.value ?? '',
      createdAt: post.created_at,
      diedAt: post.is_expired ? post.expire_at : null,
      voteCount: post.vote_count ?? 0,
      replyCount: post.reply_count ?? 0,
      isExpired: post.is_expired || DateTime.fromISO(post.expire_at) < DateTime.now(),
    });
    try { setDataUrl(c.toDataURL('image/png')); } catch { /* cross-origin canvas */ }
  }, [open, post, username]);

  const handleDownload = () => {
    hapticTap();
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `wipe-epitaph-${post.id.slice(0, 8)}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleShare = async () => {
    hapticTap();
    if (!dataUrl) return;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `wipe-epitaph-${post.id.slice(0, 8)}.png`, { type: 'image/png' });
      if ('share' in navigator && 'canShare' in navigator && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'wipe.haus — an epitaph',
          text: `Remembering this post on wipe.haus`,
        });
        return;
      }
    } catch { /* fallthrough */ }
    handleDownload();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: 'background.paper' } }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, pb: 0 }}>
        <Typography sx={{ fontFamily: '"VT323", monospace', letterSpacing: 2, fontSize: '1.2rem' }}>
          EPITAPH PREVIEW
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <canvas
            ref={canvasRef}
            width={1200}
            height={628}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 0,
              border: '2px solid currentColor',
            }}
          />
        </Box>
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ textTransform: 'none' }}
          >
            Download PNG
          </Button>
          <Button
            variant="outlined"
            startIcon={<IosShareIcon />}
            onClick={handleShare}
            sx={{ textTransform: 'none' }}
          >
            Share
          </Button>
        </Stack>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5, textAlign: 'center', fontStyle: 'italic' }}>
          A physical artifact of something that doesn't exist anymore.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

export default DeathCertificate;
